import React, {useMemo, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {FiAlertCircle, FiArrowLeft, FiCheck, FiEye, FiEyeOff, FiLock, FiMail, FiRefreshCw, FiShield} from 'react-icons/fi';
import AppHeader from '../components/AppHeader/AppHeader';
import {accountsApi, authStorage, getErrorFields, getErrorMessage} from '../services/api';
import './Login.css';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function RecoveryStory() {
    return <aside className="auth-story auth-recovery-story">
        <div className="auth-story-orb auth-story-orb-one"/><div className="auth-story-orb auth-story-orb-two"/>
        <div className="auth-story-content">
            <span className="auth-eyebrow"><FiShield/> بازیابی امن حساب</span>
            <h1>دوباره به مسیر<br/><strong>یادگیری برگردید</strong></h1>
            <p>لینک بازیابی فقط برای مدت محدود معتبر است و پس از تغییر رمز، نشست‌های قبلی حساب بسته می‌شوند.</p>
            <ul>
                <li><span><FiMail/></span><div><strong>ارسال به ایمیل حساب</strong><small>لینک اختصاصی و یک‌بارمصرف</small></div></li>
                <li><span><FiLock/></span><div><strong>رمز عبور تازه</strong><small>حداقل ۸ نویسه و غیرقابل حدس</small></div></li>
                <li><span><FiShield/></span><div><strong>خروج از نشست‌های قبلی</strong><small>برای محافظت بیشتر از حساب شما</small></div></li>
            </ul>
        </div>
    </aside>;
}

function RecoveryLayout({title, description, children}) {
    return <div className="auth-page">
        <AppHeader/>
        <main className="auth-main">
            <section className="auth-shell auth-recovery-shell" aria-labelledby="recovery-title">
                <RecoveryStory/>
                <div className="auth-panel auth-recovery-panel">
                    <div className="auth-panel-header"><span className="auth-mobile-kicker">سامانه آزمون مجازی سام</span><h2 id="recovery-title">{title}</h2><p>{description}</p></div>
                    {children}
                </div>
            </section>
        </main>
    </div>;
}

function PasswordInput({id, label, value, onChange, error, autoFocus = false}) {
    const [visible, setVisible] = useState(false);
    return <label className="auth-field" htmlFor={id}>
        <span className="auth-field-label">{label}</span>
        <span className={`auth-input-wrap ${error ? 'has-error' : ''}`}>
            <FiLock aria-hidden="true"/>
            <input id={id} name={id} type={visible ? 'text' : 'password'} value={value} onChange={onChange} minLength={8} autoComplete="new-password" autoFocus={autoFocus} required aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined}/>
            <button type="button" className="auth-password-toggle" onClick={() => setVisible((current) => !current)} aria-label={visible ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}>{visible ? <FiEyeOff/> : <FiEye/>}</button>
        </span>
        {error && <span className="auth-field-error" id={`${id}-error`}><FiAlertCircle/>{error}</span>}
    </label>;
}

const rateLimitMessage = (error) => error?.retryAfter
    ? `تعداد درخواست‌ها بیش از حد مجاز است؛ ${error.retryAfter} ثانیه دیگر دوباره تلاش کنید.`
    : 'تعداد درخواست‌ها بیش از حد مجاز است؛ لطفاً کمی بعد دوباره تلاش کنید.';

export function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [fieldError, setFieldError] = useState('');
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        if (busy) return;
        const cleanEmail = email.trim();
        if (!emailPattern.test(cleanEmail)) {
            setFieldError('یک نشانی ایمیل معتبر وارد کنید.');
            return;
        }
        setBusy(true); setError(''); setFieldError('');
        try {
            await accountsApi.requestPasswordReset(cleanEmail);
            setSubmitted(true);
        } catch (requestError) {
            const fields = getErrorFields(requestError);
            if (fields.email?.length) setFieldError(fields.email[0]);
            else setError(requestError.status === 429 ? rateLimitMessage(requestError) : getErrorMessage(requestError));
        } finally { setBusy(false); }
    };

    return <RecoveryLayout title="بازیابی رمز عبور" description="ایمیل حساب خود را وارد کنید تا لینک تعیین رمز جدید برایتان ارسال شود.">
        {submitted ? <div className="auth-recovery-result" role="status">
            <span><FiCheck/></span><h3>درخواست ثبت شد</h3>
            <p>اگر حساب فعالی با این ایمیل وجود داشته باشد، لینک بازیابی برای آن ارسال شد.</p>
            <small>پوشه spam یا junk را هم بررسی کنید. اعتبار لینک محدود است.</small>
            <button type="button" className="auth-submit" onClick={() => { setSubmitted(false); setEmail(''); }}><FiRefreshCw/> استفاده از ایمیل دیگر</button>
        </div> : <>
            {error && <div className="auth-alert error" role="alert"><FiAlertCircle/><span>{error}</span></div>}
            <form className="auth-form auth-recovery-form" onSubmit={submit} noValidate>
                <label className="auth-field" htmlFor="recovery-email">
                    <span className="auth-field-label">ایمیل حساب</span>
                    <span className={`auth-input-wrap ${fieldError ? 'has-error' : ''}`}><FiMail/><input id="recovery-email" type="email" dir="ltr" autoComplete="email" autoFocus value={email} onChange={(event) => { setEmail(event.target.value); setFieldError(''); setError(''); }} placeholder="name@example.com" aria-invalid={Boolean(fieldError)} required/></span>
                    {fieldError && <span className="auth-field-error"><FiAlertCircle/>{fieldError}</span>}
                </label>
                <button className="auth-submit" type="submit" disabled={busy}>{busy ? <><span className="auth-spinner"/>در حال ارسال...</> : <>ارسال لینک بازیابی<FiArrowLeft/></>}</button>
            </form>
        </>}
        <p className="auth-switch-copy"><Link to="/login">بازگشت به صفحه ورود</Link></p>
    </RecoveryLayout>;
}

export function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const uid = params.get('uid');
    const token = params.get('token');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState('');
    const [linkInvalid, setLinkInvalid] = useState(!uid || !token);
    const [busy, setBusy] = useState(false);
    const passwordLengthValid = useMemo(() => password.length >= 8, [password]);

    const updatePassword = (setter, field) => (event) => {
        setter(event.target.value);
        setFieldErrors((current) => ({...current, [field]: ''}));
        setError('');
    };
    const submit = async (event) => {
        event.preventDefault();
        if (busy || linkInvalid) return;
        const nextErrors = {};
        if (!passwordLengthValid) nextErrors.password = 'رمز عبور باید حداقل ۸ نویسه داشته باشد.';
        if (!confirmation) nextErrors.confirmation = 'تکرار رمز عبور را وارد کنید.';
        else if (confirmation !== password) nextErrors.confirmation = 'رمز عبور و تکرار آن یکسان نیستند.';
        if (Object.keys(nextErrors).length) { setFieldErrors(nextErrors); return; }
        setBusy(true); setError('');
        try {
            await accountsApi.confirmPasswordReset({uid, token, new_password: password});
            authStorage.clear();
            navigate('/login', {replace: true, state: {passwordResetSuccess: true}});
        } catch (requestError) {
            const fields = getErrorFields(requestError);
            const passwordMessages = [...(fields.new_password || []), ...(fields.password || [])];
            const nonFieldMessages = fields.non_field_errors || [];
            const rawMessages = [...passwordMessages, ...nonFieldMessages].join(' ');
            if (/invalid|expired|نامعتبر|منقضی/i.test(rawMessages)) {
                setLinkInvalid(true);
                setError('این لینک بازیابی نامعتبر، منقضی یا قبلاً استفاده شده است.');
            } else if (passwordMessages.length || nonFieldMessages.length) {
                setFieldErrors((current) => ({...current, password: [...passwordMessages, ...nonFieldMessages].join(' ')}));
            } else {
                setError(requestError.status === 429 ? rateLimitMessage(requestError) : getErrorMessage(requestError));
            }
        } finally { setBusy(false); }
    };

    return <RecoveryLayout title="تعیین رمز عبور جدید" description="رمزی تازه و امن برای حساب خود انتخاب کنید.">
        {error && <div className="auth-alert error" role="alert"><FiAlertCircle/><span>{error}</span></div>}
        {linkInvalid ? <div className="auth-invalid-link">
            <span><FiAlertCircle/></span><h3>لینک بازیابی قابل استفاده نیست</h3><p>ممکن است لینک ناقص، منقضی یا قبلاً استفاده شده باشد.</p><Link className="auth-submit" to="/forgot-password">درخواست لینک جدید<FiArrowLeft/></Link>
        </div> : <form className="auth-form auth-recovery-form" onSubmit={submit} noValidate>
            <PasswordInput id="new-password" label="رمز عبور جدید" value={password} onChange={updatePassword(setPassword, 'password')} error={fieldErrors.password} autoFocus/>
            <div className={`auth-password-minimum ${passwordLengthValid ? 'passed' : ''}`}><FiCheck/> حداقل ۸ نویسه</div>
            <PasswordInput id="confirm-new-password" label="تکرار رمز عبور جدید" value={confirmation} onChange={updatePassword(setConfirmation, 'confirmation')} error={fieldErrors.confirmation}/>
            <button className="auth-submit" type="submit" disabled={busy}>{busy ? <><span className="auth-spinner"/>در حال ثبت...</> : <>ثبت رمز جدید<FiArrowLeft/></>}</button>
        </form>}
        <p className="auth-switch-copy"><Link to="/login">بازگشت به صفحه ورود</Link></p>
    </RecoveryLayout>;
}
